import { cn } from "@/lib/utils";

interface FinPlayLogoProps {
  size?: "sm" | "md" | "lg" | "xl";
  animated?: boolean;
  showText?: boolean;
  className?: string;
}

const sizeMap = {
  sm: { container: 32, inner: 18, text: "text-sm" },
  md: { container: 48, inner: 26, text: "text-lg" },
  lg: { container: 80, inner: 44, text: "text-2xl" },
  xl: { container: 120, inner: 66, text: "text-3xl" },
};

const FinPlayLogo = ({ 
  size = "md", 
  animated = false, 
  showText = false,
  className 
}: FinPlayLogoProps) => {
  const dimensions = sizeMap[size];
  const strokeWidth = size === "sm" ? 2 : size === "md" ? 2.5 : 3;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div 
        className="relative flex items-center justify-center"
        style={{ 
          width: dimensions.container, 
          height: dimensions.container 
        }}
      >
        {/* Animated outer ring */}
        <svg 
          viewBox="0 0 100 100" 
          className={cn(
            "absolute inset-0 w-full h-full",
            animated && "animate-spin-slow"
          )}
          style={{ animationDuration: "12s" }}
        >
          {/* Three colored arc segments */}
          <defs>
            <linearGradient id="tealGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(170 85% 45%)" />
              <stop offset="100%" stopColor="hsl(160 80% 50%)" />
            </linearGradient>
            <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(45 90% 55%)" />
              <stop offset="100%" stopColor="hsl(38 95% 52%)" />
            </linearGradient>
            <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(280 70% 60%)" />
              <stop offset="100%" stopColor="hsl(270 85% 60%)" />
            </linearGradient>
          </defs>
          
          {/* Teal segment (Fin-der) */}
          <path
            d="M 50 8 A 42 42 0 0 1 86.3 71"
            fill="none"
            stroke="url(#tealGradient)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          
          {/* Gold segment (Fantasy League) */}
          <path
            d="M 86.3 71 A 42 42 0 0 1 13.7 71"
            fill="none"
            stroke="url(#goldGradient)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          
          {/* Purple segment (Community) */}
          <path
            d="M 13.7 71 A 42 42 0 0 1 50 8"
            fill="none"
            stroke="url(#purpleGradient)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        </svg>

        {/* Inner logo mark - Stylized F with chart */}
        <div 
          className={cn(
            "relative z-10 flex items-center justify-center rounded-full",
            "bg-gradient-to-br from-background to-muted/50",
            "dark:from-[hsl(220_20%_12%)] dark:to-[hsl(220_25%_8%)]",
            " dark:shadow-[inset_0_0_20px_hsl(170_80%_50%/0.1)]"
          )}
          style={{ 
            width: dimensions.inner, 
            height: dimensions.inner 
          }}
        >
          <svg 
            viewBox="0 0 24 24" 
            className="w-3/5 h-3/5"
            fill="none"
          >
            {/* Stylized F with upward trend */}
            <path
              d="M 6 18 L 6 6 L 14 6"
              stroke="hsl(170 85% 45%)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M 6 12 L 11 12"
              stroke="hsl(170 85% 45%)"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            {/* Upward trend line */}
            <path
              d="M 10 18 L 14 13 L 18 15 L 22 8"
              stroke="hsl(45 90% 55%)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={cn(animated && "animate-pulse")}
            />
            {/* Arrow head */}
            <path
              d="M 19 8 L 22 8 L 22 11"
              stroke="hsl(45 90% 55%)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Glow effect for animated version */}
        {animated && (
          <div 
            className="absolute inset-0 rounded-full blur-xl opacity-30 animate-pulse"
            style={{
              background: "radial-gradient(circle, hsl(170 85% 45% / 0.5) 0%, transparent 70%)"
            }}
          />
        )}
      </div>

      {/* Text logo */}
      {showText && (
        <span className={cn(
          dimensions.text,
          "font-display font-semibold tracking-tight",
          "bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent"
        )}>
          FinPlay
        </span>
      )}
    </div>
  );
};

export default FinPlayLogo;
